/**
 * Auto Rate Analysis — Epic E.
 *
 * Paste a BOQ description + unit → backend searches Rate Library first
 * (`source: "library"`), falls back to Claude Sonnet 4 AI (`source: "ai"`).
 * Returns full Materials/Labour/Equipment breakdown for the rate, plus
 * optional "Save to library" button when AI generated the result.
 */
import React, { useState } from 'react';
import { apiPost } from '../../lib/api';
import {
  Sparkles, Search, Loader2, BookOpen, Hammer, Wrench, Building2,
  IndianRupee, MapPin, Plus, Save, ChevronDown, ChevronUp, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function AutoRateAnalysis() {
  const [form, setForm] = useState({ description: '', unit: 'cum', qty: '1', location: 'Mumbai' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({ materials: true, labour: true, equipment: true });

  const handleAnalyse = async (e) => {
    e?.preventDefault();
    if (!form.description.trim()) { toast.error('Please enter a BOQ description'); return; }
    setLoading(true); setResult(null);
    try {
      const res = await apiPost('/rate-analysis/auto', {
        description: form.description.trim(),
        unit: form.unit.trim(),
        qty: Number(form.qty) || 1,
        location: form.location.trim() || 'Mumbai',
        save_to_library: false,
      });
      setResult(res);
      toast.success(res.source === 'library' ? 'Matched in Rate Library' : 'AI rate analysis ready');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Analysis failed');
    } finally { setLoading(false); }
  };

  const handleSaveToLibrary = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const res = await apiPost('/rate-analysis/auto', {
        description: form.description.trim(),
        unit: form.unit.trim(),
        qty: Number(form.qty) || 1,
        location: form.location.trim() || 'Mumbai',
        save_to_library: true,
      });
      setResult(res);
      toast.success('Saved to Rate Library · ID ' + (res.saved_library_item_id || '').slice(0, 8));
    } catch (err) {
      toast.error('Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Estimation · AI-Powered</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-3">
            Auto Rate Analysis <Sparkles size={28} className="text-amber-500" />
          </h1>
          <p className="text-sm text-slate-600 mt-1">Paste any BOQ line description — get an instant rate breakdown from the Rate Library or fresh from Ellora AI.</p>
        </div>
      </div>

      <form onSubmit={handleAnalyse} className="bg-white border border-slate-200 rounded-sm p-5 mb-5" data-testid="ara-form">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-7">
            <label className="label">BOQ Description *</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder='e.g. "Providing and laying Plain Cement Concrete 1:4:8 with 40mm aggregate including formwork, curing, finishing"'
              className="input" rows={3} required data-testid="ara-description" />
          </div>
          <div className="md:col-span-2">
            <label className="label">Unit *</label>
            <input type="text" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="input" required data-testid="ara-unit" placeholder="cum / sqm / kg" />
          </div>
          <div className="md:col-span-1">
            <label className="label">Qty</label>
            <input type="number" step="0.01" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="input" data-testid="ara-qty" />
          </div>
          <div className="md:col-span-2">
            <label className="label">Location</label>
            <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input" data-testid="ara-location" placeholder="City" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button type="submit" className="btn-primary flex items-center gap-2" disabled={loading} data-testid="ara-analyse-btn">
            {loading ? <><Loader2 size={14} className="animate-spin" /> Analysing…</> : <><Search size={14} /> Run Analysis</>}
          </button>
        </div>
      </form>

      {result && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="ara-result">
          <div className={`px-5 py-4 flex flex-wrap items-center justify-between gap-3 ${result.source === 'library' ? 'bg-emerald-50 border-b border-emerald-200' : 'bg-amber-50 border-b border-amber-200'}`}>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-600 flex items-center gap-2">
                {result.source === 'library' ? <><BookOpen size={11} /> Matched in Rate Library</> : <><Sparkles size={11} /> Generated by Ellora AI</>}
                {result.match_score != null && <span className="text-emerald-700">· Match score {Math.round(result.match_score * 100) / 100}</span>}
              </div>
              <div className="font-display text-xl font-extrabold text-[#0F172A] mt-1">{result.item_name}</div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                <span className="tag bg-white border border-slate-200 text-slate-700">{result.category}</span>
                {result.location && <span className="flex items-center gap-1"><MapPin size={11} /> {result.location}</span>}
                {(result.keywords || []).slice(0, 6).map((k, i) => <span key={i} className="text-[10px] text-slate-500">#{k}</span>)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-600">Rate / {form.unit}</div>
              <div className="font-display text-3xl font-extrabold text-[#3b4872]">{fmt(result.base_rate)}</div>
              <div className="text-xs text-slate-600">Total: <span className="font-display font-bold text-[#0F172A]">{fmt(result.total_amount)}</span></div>
            </div>
          </div>

          {result.source === 'ai' && !result.saved_library_item_id && (
            <div className="bg-blue-50 border-b border-blue-200 px-5 py-3 flex items-center justify-between gap-3" data-testid="ara-save-banner">
              <div className="flex items-center gap-2 text-xs text-blue-900">
                <AlertCircle size={14} /> This rate was generated by AI. Save it to your Rate Library so future similar BOQ lines match in milliseconds.
              </div>
              <button onClick={handleSaveToLibrary} disabled={saving} className="btn-primary text-xs flex items-center gap-2" data-testid="ara-save-btn">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save to Rate Library
              </button>
            </div>
          )}
          {result.saved_library_item_id && (
            <div className="bg-emerald-50 border-b border-emerald-200 px-5 py-3 text-xs text-emerald-800">
              ✅ Saved to Rate Library — ID <span className="font-mono">{result.saved_library_item_id.slice(0, 8)}</span>. Next time this BOQ line will match instantly.
            </div>
          )}

          <div className="p-5 space-y-4">
            <BreakdownSection title="Materials" icon={Building2} rows={result.materials} kind="materials"
              expanded={expanded.materials} onToggle={() => setExpanded({ ...expanded, materials: !expanded.materials })} />
            <BreakdownSection title="Labour" icon={Hammer} rows={result.labour} kind="labour"
              expanded={expanded.labour} onToggle={() => setExpanded({ ...expanded, labour: !expanded.labour })} />
            <BreakdownSection title="Equipment" icon={Wrench} rows={result.equipment} kind="equipment"
              expanded={expanded.equipment} onToggle={() => setExpanded({ ...expanded, equipment: !expanded.equipment })} />

            {result.notes && (
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-700">
                <div className="font-bold text-[10px] uppercase tracking-wider mb-1">Notes</div>
                {result.notes}
              </div>
            )}
          </div>
        </div>
      )}

      {!result && !loading && (
        <div className="bg-white border border-dashed border-slate-300 rounded-sm p-10 text-center">
          <Sparkles size={32} className="mx-auto text-amber-400 mb-3" />
          <div className="font-display font-bold text-slate-700 mb-1">Ready to analyse</div>
          <div className="text-sm text-slate-500">Paste any BOQ description above and click <b>Run Analysis</b> to get an instant rate breakdown.</div>
          <div className="mt-4 text-xs text-slate-500 flex items-center justify-center gap-4 flex-wrap">
            <span className="flex items-center gap-1"><BookOpen size={12} /> Library match</span>
            <span className="text-slate-300">|</span>
            <span className="flex items-center gap-1"><Sparkles size={12} className="text-amber-500" /> AI fallback</span>
            <span className="text-slate-300">|</span>
            <span className="flex items-center gap-1"><Save size={12} /> Save & reuse</span>
          </div>
        </div>
      )}
    </div>
  );
}

const BreakdownSection = ({ title, icon: Icon, rows, kind, expanded, onToggle }) => {
  const total = (rows || []).reduce((s, r) => s + (r.amount || 0), 0);
  if (!rows || rows.length === 0) return (
    <div className="border border-slate-200 rounded-sm">
      <div className="px-3 py-2 bg-slate-50 text-xs text-slate-500 flex items-center gap-2"><Icon size={12} /> {title} · None</div>
    </div>
  );
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid={`ara-section-${kind}`}>
      <button type="button" onClick={onToggle} className="w-full px-4 py-2.5 bg-slate-50 hover:bg-slate-100 flex items-center justify-between text-left">
        <div className="font-display font-bold text-sm flex items-center gap-2 text-[#0F172A]">
          <Icon size={14} className="text-[#3b4872]" /> {title}
          <span className="tag bg-white border border-slate-200 text-slate-600 text-[10px]">{rows.length}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm font-display font-bold text-[#3b4872]">{fmt(total)}</div>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </button>
      {expanded && (
        <table className="w-full dense-table border-t border-slate-200">
          <thead>
            <tr>
              <th>Name</th>
              <th className="text-right">{kind === 'materials' ? 'Qty / Unit' : 'Hours / Unit'}</th>
              <th>Unit</th>
              <th className="text-right">Rate (₹)</th>
              {kind === 'materials' && <th className="text-right">Wastage %</th>}
              <th className="text-right">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.name}</td>
                <td className="text-right">{r.qty_per_unit || r.hours_per_unit}</td>
                <td>{r.unit}</td>
                <td className="text-right">{fmt(r.rate)}</td>
                {kind === 'materials' && <td className="text-right">{r.wastage_pct || 0}%</td>}
                <td className="text-right font-display font-bold">{fmt(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
